import { Component, OnInit, Inject } from '@angular/core';
import { ProfileService } from 'app/profile/services/profile.service';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ToolParameter, ToolParameterType, ToolCategory, ToolSubcategory } from 'app/profile/models/generic.model';
import { Observable, combineLatest } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { GeneralSettingsState } from 'app/profile/store/state/general.state';
import { GetToolCategories, GetToolSubCategories } from 'app/profile/store/app.actions';
import { SelectToolCategories, SelectToolSubCategories } from 'app/profile/store/app.selector';
import { TslSelectChange, TslDialog } from '@tesla/ng-tds';
import { CustomValidator } from 'app/shared/custom-validator/custom-validator';
import { I18nService } from 'app/shared/services/i18n.service';

@Component({
	selector: 'app-parameter-type-dialog',
	templateUrl: './parameter-type-dialog.component.html',
	styleUrls: ['./parameter-type-dialog.component.scss']
})
export class ParameterTypeDialogComponent implements OnInit {
	obParameterTypes$: Observable<ToolParameterType[]>;
	filterCategories: ToolCategory[];
	filterSubcategories: ToolSubcategory[];
	allSubcategories: ToolSubcategory[];
	paramGroup: FormGroup;

	get parameterType() { return this.paramGroup.get('parameterType'); }
	get toolCategory() { return this.paramGroup.get('toolCategory'); }
	get toolSubcategory() { return this.paramGroup.get('toolSubcategory'); }
	get paramValue() { return this.paramGroup.get('paramValue'); }

	get role() { return this.data ? this.data.role : ""; }

	constructor(
		private fb: FormBuilder,
		private service: ProfileService,
		private store: Store<GeneralSettingsState>,
		private dialog: TslDialog,
		public translate: I18nService,
		public dialogRef: MatDialogRef<ParameterTypeDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data:
			{
				supplierCode: string,
				categoryId: number,
				subCategoryId: number,
				role: string
			}) {
		this.paramGroup = this.fb.group({
			toolCategory: ['', Validators.required],
			toolSubcategory: ['', Validators.required],
			parameterType: ['', [Validators.required, CustomValidator.noWhitespaceValidator, Validators.maxLength(100)]],
			paramValue: ['', null]
		});
	}

	ngOnInit() {
		this.loadResources();

		this.paramGroup.patchValue({
			toolCategory: this.data.categoryId,
			toolSubcategory: this.data.subCategoryId
		});

		if (this.toolCategory && this.toolCategory.value > 0) {
			this.filterSubcategories = JSON.parse(JSON.stringify(this.allSubcategories
				.filter(x => x.toolCategoryId === this.toolCategory.value)));
		}
	}

	onClose(): void {
		this.dialogRef.close();
	}

	onSave(): void {
		if (this.paramGroup.invalid) {
			return;
		}

		const toolParamType: ToolParameterType | any = {
			id: 0,
			supplierCode: this.data.supplierCode,
			toolSubcategoryId: this.paramGroup.value.toolSubcategory,
			type: this.paramGroup.value.parameterType,
			isDefault: false,
			status: null,
			value: this.paramGroup.value.paramValue
		};

		this.service.postParameterType(toolParamType).subscribe(res => {
			if (!res) {
				this.dialog.alert(this.translate.get('Error'), this.translate.get('Adding characteristic failed.'));
				this.dialogRef.close();
			}
			else {
				toolParamType.id = res.id;
				this.dialogRef.close(toolParamType);
			}
		});
	}

	private loadResources(): void {
		this.store.dispatch(new GetToolCategories());
		this.store.dispatch(new GetToolSubCategories(this.data.supplierCode, false));
		combineLatest(
			[this.store.select(SelectToolCategories),
			this.store.select(SelectToolSubCategories)])
			.subscribe(([categories, subCategories]) => {
				if (categories && subCategories) {
					this.filterCategories = categories.sort((a, b) =>
						a.category.trim().toLowerCase().localeCompare(b.category.trim().toLowerCase(), undefined, { sensitivity: 'base' }));
					this.allSubcategories = subCategories[this.data.supplierCode].sort((a, b) =>
						a.subcategory.trim().toLowerCase().localeCompare(b.subcategory.trim().toLowerCase(), undefined, { sensitivity: 'base' }));
				}
			});
	}

	onChangeCategory(event: TslSelectChange): void {
		const categoryId = parseInt(event.value || 0, 0);
		this.paramValue.enable();
		this.filterSubcategories = JSON.parse(JSON.stringify(this.allSubcategories
			.filter(x => x.toolCategoryId === categoryId || categoryId === 0)));
		if (event.value !== this.data.categoryId) {
			this.paramValue.disable();
		}
	}

	onChangeSubcategory(event: TslSelectChange): void {
		this.paramValue.enable();
		if (event.value !== this.data.subCategoryId) {
			this.paramValue.disable();
		}
	}
}
